import { RefreshCw, Check, AlertTriangle, Clock } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { useApiFallback } from "../../hooks/useApiFallback";
import {
  getLocalSyncHistory,
  triggerLocalSync,
} from "../../api/endpoints";
import { ApiError } from "../../api/types";
import type { LocalSyncHistoryItem } from "../../api/types";
import { NODE_INFO } from "../../data";

// Fallback — несколько искусственных событий sync.*, чтобы UI оставался читаемым,
// если бэкенд недоступен.
const FALLBACK_HISTORY: LocalSyncHistoryItem[] = [
  {
    id: "evt-882134",
    time_h: "14:42",
    when_rel: "3 мин",
    text: "синхронизирован → поколение 482",
    detail: "1 артефакт · 18.4 МБ",
    type: "sync.complete",
    source: "msk-01",
    target: "global",
  },
  {
    id: "evt-882130",
    time_h: "14:30",
    when_rel: "18 мин",
    text: "плановый запрос обмена",
    detail: null,
    type: "sync.requested",
    source: "msk-01",
    target: "global",
  },
  {
    id: "evt-882124",
    time_h: "14:25",
    when_rel: "23 мин",
    text: "нет связи с vld-01 (4 попытки подряд)",
    detail: null,
    type: "sync.failed",
    source: "msk-01",
    target: "vld-01",
  },
];

export default function SyncPage() {
  const push = useToast();
  const qc = useQueryClient();

  const historyQ = useQuery({
    queryKey: ["local", "sync", "history"],
    queryFn: getLocalSyncHistory,
    refetchInterval: 15_000,
  });
  const history = useApiFallback<LocalSyncHistoryItem[]>(
    historyQ.data,
    historyQ.isError,
    FALLBACK_HISTORY,
  );

  const syncMut = useMutation({
    mutationFn: triggerLocalSync,
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["local", "sync", "history"] });
      await qc.invalidateQueries({ queryKey: ["local", "status"] });
      push({ kind: "success", text: res?.message ?? "Принудительная синхронизация запрошена" });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : "Не удалось запустить синхронизацию";
      push({ kind: "warn", text: msg });
    },
  });

  const items = history.value;
  const lastComplete = items.find((e) => e.type === "sync.complete");
  const lastFailed = items.find((e) => e.type === "sync.failed");

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Синхронизация каталога
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Обмен с Глобальным сервером и соседями каждые 10 минут
          </div>
        </div>
        <Btn
          variant="primary"
          icon={RefreshCw}
          onClick={() => syncMut.mutate()}
          iconClass={syncMut.isPending ? "spin" : ""}
        >
          {syncMut.isPending ? "Синхронизация…" : "Принудительный синк"}
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Глобал — карточка состояния */}
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">С Глобальным сервером</h2>
          <dl className="mt-4 space-y-2.5 text-[12.5px]">
            <Row label="Адрес" value={<span className="font-mono">{NODE_INFO.globalAddress}</span>} />
            <Row label="Сертификат" value={
              <span className="rounded-full bg-[var(--color-success-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-success)]">
                Активен · {NODE_INFO.certExpires}
              </span>
            } />
            <Row label="Поколение каталога" value={<span className="font-mono font-medium">{NODE_INFO.generation}</span>} />
            <Row label="Последний sync.complete" value={
              lastComplete
                ? <span className="text-[var(--color-success)]">{lastComplete.when_rel} назад</span>
                : <span className="text-[var(--color-fg-muted)]">нет данных</span>
            } />
            <Row label="Последний sync.failed" value={
              lastFailed
                ? <span className="text-[var(--color-danger)]">{lastFailed.when_rel} назад</span>
                : <span className="text-[var(--color-fg-muted)]">не было</span>
            } />
          </dl>
        </section>

        {/* Сводка из журнала */}
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">Сводка</h2>
          <ul className="mt-4 space-y-2.5 text-[12.5px]">
            <Bucket
              icon={<Check size={12} />}
              kind="ok"
              label="успешных синхронизаций"
              value={items.filter((e) => e.type === "sync.complete").length}
            />
            <Bucket
              icon={<Clock size={12} />}
              kind="busy"
              label="запросов на синхронизацию"
              value={items.filter((e) => e.type === "sync.requested").length}
            />
            <Bucket
              icon={<AlertTriangle size={12} />}
              kind="err"
              label="неудач"
              value={items.filter((e) => e.type === "sync.failed").length}
            />
          </ul>
        </section>
      </div>

      {/* История событий */}
      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-[var(--color-fg)]">История синхронизаций</h2>
          <span className="text-[11.5px] text-[var(--color-fg-muted)]">
            {items.length} событий{historyQ.isFetching ? " · обновляется…" : ""}
          </span>
        </div>
        <div className="-mx-1 mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <Th>Время</Th><Th>Тип</Th><Th>Источник</Th><Th>Цель</Th><Th>Текст</Th><Th>ID</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((e, i) => (
                <tr
                  key={e.id}
                  style={{ animationDelay: `${Math.min(i, 8) * 25}ms` }}
                  className="stagger-up border-t border-[var(--color-border)]"
                >
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{e.time_h}</td>
                  <td className="whitespace-nowrap px-3 py-2.5"><TypeChip type={e.type} /></td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">{e.source}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{e.target ?? "—"}</td>
                  <td className="px-3 py-2.5 text-[var(--color-fg)]">{e.text}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-[11px] text-[var(--color-fg-faint)]">{e.id}</td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                  События синхронизации пока не зарегистрированы.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-dashed border-[var(--color-border)] pb-2.5 last:border-0 last:pb-0">
      <dt className="text-[var(--color-fg-muted)]">{label}</dt>
      <dd className="text-right text-[var(--color-fg)]">{value}</dd>
    </div>
  );
}

function Bucket({
  icon,
  kind,
  label,
  value,
}: {
  icon: React.ReactNode;
  kind: "ok" | "busy" | "err";
  label: string;
  value: number;
}) {
  const tone =
    kind === "ok"
      ? { bg: "var(--color-success-bg)", fg: "var(--color-success)" }
      : kind === "busy"
      ? { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" }
      : { bg: "var(--color-danger-bg)", fg: "var(--color-danger)" };
  return (
    <li className="flex items-center gap-3">
      <span
        className="grid h-5 w-5 shrink-0 place-items-center rounded-full"
        style={{ background: tone.bg, color: tone.fg }}
      >
        {icon}
      </span>
      <div className="flex-1 text-[var(--color-fg)]">{label}</div>
      <div className="font-mono text-[var(--color-fg)]">{value}</div>
    </li>
  );
}

function TypeChip({ type }: { type: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    "sync.complete":  { bg: "var(--color-success-bg)", fg: "var(--color-success)" },
    "sync.requested": { bg: "var(--color-surface-3)",  fg: "var(--color-fg-muted)" },
    "sync.failed":    { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)" },
  };
  const m = map[type] ?? { bg: "var(--color-surface-3)", fg: "var(--color-fg-muted)" };
  return (
    <span
      className="rounded px-1.5 py-0.5 font-mono text-[10px] font-medium"
      style={{ background: m.bg, color: m.fg }}
    >
      {type}
    </span>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
